/**
 * Marine area calculation — the one genuinely new pricing construct.
 *
 * A marine service prices as `area x rate` instead of reading a flat S/M/L/XL
 * matrix. Each marine `coverage_level` declares an AREA BASIS: the formula that
 * turns a boat's dimensions into that coverage's square footage.
 *
 * ZERO IMPORTS BY DESIGN — unit-tested directly by
 * `scripts/test/marineCalc.test.mjs`. Do not add imports.
 *
 * Factors and hull-height priors are per-installer, server-supplied
 * (`service.marine_factor`); the seed values live in the server catalog so
 * installers can tune them without a client deploy.
 */

export const AREA_BASIS = {
  FOOTPRINT: 'footprint',   // LOA x beam x factor        — horizontal surfaces
  VERTICAL: 'vertical',     // LOA x hull height x factor — topsides
  COMPOSITE: 'composite',   // LOA x beam x full-craft factor
  PRIOR: 'prior',           // class prior or estimator entry — not derivable
};

export const COVERAGE_AREA_BASIS = {
  DECK: AREA_BASIS.FOOTPRINT,
  BOTTOM: AREA_BASIS.FOOTPRINT,
  BOW: AREA_BASIS.FOOTPRINT,
  HULL_SIDES: AREA_BASIS.VERTICAL,
  TRANSOM: AREA_BASIS.VERTICAL,
  GUNWALES: AREA_BASIS.VERTICAL,
  FULL_ABOVE_WATERLINE: AREA_BASIS.COMPOSITE,
  FULL_HULL: AREA_BASIS.COMPOSITE,
  GLASS: AREA_BASIS.PRIOR,
  HELM_CONSOLE: AREA_BASIS.PRIOR,
  COWLINGS: AREA_BASIS.PRIOR,
  HARDWARE: AREA_BASIS.PRIOR,
};

export const MARINE_COVERAGE_LABELS = {
  DECK: 'Deck / Topside',
  BOTTOM: 'Bottom',
  BOW: 'Bow',
  HULL_SIDES: 'Hull Sides',
  TRANSOM: 'Transom',
  GUNWALES: 'Gunwales',
  FULL_ABOVE_WATERLINE: 'Full Above Waterline',
  FULL_HULL: 'Full Hull',
  GLASS: 'Glass',
  HELM_CONSOLE: 'Helm / Console',
  COWLINGS: 'Cowlings',
  HARDWARE: 'Hardware',
};

function require_(vars, name) {
  const v = vars[name];
  if (v === null || v === undefined || v === '' || Number.isNaN(Number(v))) {
    throw new Error(`Marine area calculation requires "${name}"`);
  }
  return Number(v);
}

export function footprintArea(vars) {
  return require_(vars, 'loaFt') * require_(vars, 'beamFt') * require_(vars, 'factor');
}

export function verticalArea(vars) {
  return require_(vars, 'loaFt') * (require_(vars, 'hullHeightIn') / 12) * require_(vars, 'factor');
}

/**
 * `prior` coverages have no geometric derivation — the caller supplies
 * `priorAreaSqFt` from a per-class prior or estimator entry.
 */
export function priorArea(vars) {
  return require_(vars, 'priorAreaSqFt');
}

export function computeArea(coverageLevel, vars = {}) {
  const basis = COVERAGE_AREA_BASIS[coverageLevel];
  if (!basis) throw new Error(`Unknown marine coverage "${coverageLevel}"`);
  switch (basis) {
    case AREA_BASIS.FOOTPRINT:
    case AREA_BASIS.COMPOSITE:
      return footprintArea(vars);
    case AREA_BASIS.VERTICAL:
      return verticalArea(vars);
    case AREA_BASIS.PRIOR:
      return priorArea(vars);
    default:
      throw new Error(`Unknown marine coverage "${coverageLevel}"`);
  }
}

/** Waste is per service/material: PPF film 15-25%, ceramic coating 0. */
export function applyWaste(areaSqFt, wastePct) {
  return areaSqFt * (1 + (Number(wastePct) || 0) / 100);
}

/**
 * Marine quantities are whole square feet. The estimate pricing engine
 * `parseInt`s `quantity` in 13 places and falls back to 1 on a zero/NaN — so
 * ceil, and never return 0.
 */
export function roundArea(areaSqFt) {
  const raw = Number(areaSqFt);
  if (!Number.isFinite(raw)) return 1;
  // Guard against IEEE-754 dust BEFORE ceiling. 110 * 1.10 evaluates to
  // 121.00000000000001, and a naive Math.ceil turns that into 122 — a
  // systematic +1 sq ft on any area that lands exactly on an integer after the
  // waste multiply. At $8.50/sq ft that is a silent overcharge on every job.
  // Round to 6dp first: far finer than any real measurement, coarse enough to
  // absorb binary representation error.
  const cleaned = Math.round(raw * 1e6) / 1e6;
  const n = Math.ceil(cleaned);
  return n > 0 ? n : 1;
}

export function lineTotal(areaSqFt, ratePerSqFt) {
  return Math.round(Number(areaSqFt) * Number(ratePerSqFt) * 100) / 100;
}

// ===========================================================================
// Area formula interpreter (the target contract)
// ===========================================================================
//
// The server sends the formula as DATA, and this evaluates it. It is never
// eval'd — the grammar is a fixed sum-of-products:
//
//   { sum: [ { product: [ {var|const, divide_by?}, ... ] }, ... ] }
//
// Sum-of-products covers every known case: hull sides and footprint are one
// product; a full-craft composite is two products summed; glass and discrete
// pieces are a single const or an estimator-supplied var.
//
// EVERY failure throws. A missing dimension, an unknown var, a malformed
// structure — all surface to the estimator. Never default a term to zero or
// skip it: `loa x beam x 1.3` silently becoming `loa x beam` is a 30%
// underquote that looks entirely plausible.

/** The only dimensions the client can source or prompt for. */
export const ALLOWED_AREA_VARS = ['loa_ft', 'beam_ft', 'hull_height_in', 'manual_area_sqft'];

/** Human labels for the prompt the calculator renders per dimension. */
export const AREA_VAR_LABELS = {
  loa_ft: 'Length overall (ft)',
  beam_ft: 'Beam (ft)',
  hull_height_in: 'Hull height / freeboard (in)',
  // Not derivable from hull geometry — a centre console and a sedan bridge of
  // the same LOA have wildly different glass. Measured or from a class prior.
  manual_area_sqft: 'Area (sq ft)',
};

/**
 * Sane ranges for estimator-entered dimensions. ADVISORY — these warn, they
 * never block. An unusual boat is possible; a typo is likelier.
 *
 * `hull_height_in` 6-120 is the server's own learning range, confirmed
 * 2026-08-06: a freeboard outside it is **silently ignored** by the write-back
 * to `boat_model.hull_height_in` — no error, the boat is still created. Without
 * a warning here the estimator would never learn that the value was dropped.
 *
 * The worse failure is nearer: an unflagged 300 prices the job off a 25 ft
 * freeboard before anyone notices it was meant to be 30.
 */
export const AREA_VAR_RANGES = {
  hull_height_in: { min: 6, max: 120, unit: 'in' },
};

/**
 * Advisory warning for a dimension the estimator typed. Returns a string or
 * null. Never throws, and an empty value is not a warning — that is the normal
 * pre-entry state, which `evaluateAreaFormula` reports separately.
 */
export function areaVarWarning(varName, value) {
  const range = AREA_VAR_RANGES[varName];
  if (!range) return null;
  if (value === null || value === undefined || String(value).trim() === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  if (n < range.min || n > range.max) {
    return `${n}${range.unit} is outside the usual ${range.min}-${range.max}${range.unit} range. ` +
      `Check this is right — it will price the job, and it will not be saved as this model's default.`;
  }
  return null;
}

function resolveTerm(term, vars) {
  if (!term || typeof term !== 'object') {
    throw new Error('Area formula contains a malformed term.');
  }
  let value;
  if (Object.prototype.hasOwnProperty.call(term, 'const')) {
    value = Number(term.const);
    if (!Number.isFinite(value)) {
      throw new Error('Area formula contains a non-numeric constant.');
    }
  } else if (term.var) {
    if (!ALLOWED_AREA_VARS.includes(term.var)) {
      throw new Error(`Area formula uses an unknown dimension "${term.var}".`);
    }
    const raw = vars ? vars[term.var] : undefined;
    if (raw === null || raw === undefined || raw === '' || !Number.isFinite(Number(raw))) {
      throw new Error(`Enter ${AREA_VAR_LABELS[term.var] || term.var} to calculate the area.`);
    }
    value = Number(raw);
  } else {
    throw new Error('Area formula term has neither a var nor a const.');
  }
  if (term.divide_by !== undefined) {
    const divisor = Number(term.divide_by);
    if (!Number.isFinite(divisor) || divisor === 0) {
      throw new Error('Area formula has an invalid divide_by.');
    }
    value = value / divisor;
  }
  return value;
}

/** Evaluate a sum-of-products structure against a dimensions object. */
export function evaluateAreaFormula(formula, vars) {
  if (!formula || !Array.isArray(formula.sum) || formula.sum.length === 0) {
    throw new Error('This marine service has no usable area formula.');
  }
  let total = 0;
  for (const group of formula.sum) {
    if (!group || !Array.isArray(group.product) || group.product.length === 0) {
      throw new Error('Area formula has a malformed product group.');
    }
    let product = 1;
    for (const term of group.product) product *= resolveTerm(term, vars);
    total += product;
  }
  return total;
}

/** Which dimensions a formula needs — drives the calculator's input set. */
export function formulaDimensions(formula) {
  const out = [];
  for (const group of (formula && formula.sum) || []) {
    for (const term of (group && group.product) || []) {
      if (term && term.var && !out.includes(term.var)) out.push(term.var);
    }
  }
  return out;
}

/**
 * TEMPORARY BRIDGE — remove once the server emits `area_formula`.
 *
 * The server currently sends only an opaque `area_basis` name. This maps the
 * one known value to its structure so HULL_SIDES prices today. Any other basis
 * throws, by design: guessing would produce a plausible wrong price.
 */
export const LEGACY_AREA_BASIS_FORMULAS = {
  loa_x_hull_height_x2: {
    sum: [{ product: [
      { var: 'loa_ft' },
      { var: 'hull_height_in', divide_by: 12 },
      { const: 2 },
    ] }],
  },
};

/** Prefer the server's structure; fall back to the bridge; otherwise throw. */
export function resolveAreaFormula(service) {
  const svc = service || {};
  if (svc.area_formula) return svc.area_formula;
  const basis = svc.area_basis;
  if (basis && LEGACY_AREA_BASIS_FORMULAS[basis]) return LEGACY_AREA_BASIS_FORMULAS[basis];
  throw new Error(
    basis
      ? `This service uses an area basis this version does not recognise yet ("${basis}"), so it cannot be priced here.`
      : 'This marine service has no area formula configured.'
  );
}

/**
 * The whole pipeline: formula -> x area_factor -> x waste -> CEIL.
 *
 * `area_factor` is an ADDITIONAL multiplier — any constant such as the x2 for
 * both hull sides is already inside the formula and must not be double-applied.
 */
export function computeMarineArea(service, dims) {
  const svc = service || {};
  const base = evaluateAreaFormula(resolveAreaFormula(svc), dims);
  const rawFactor = svc.area_factor === undefined || svc.area_factor === null ? 1 : Number(svc.area_factor);
  if (!Number.isFinite(rawFactor) || rawFactor <= 0) {
    throw new Error('Area factor must be a positive number.');
  }
  const waste = Number(svc.waste_pct);
  const wastePct = Number.isFinite(waste) ? waste : 0;
  return roundArea(base * rawFactor * (1 + wastePct / 100));
}
